import JsBarcode from 'jsbarcode';
import { BarcodeData } from './barcodeService';

// Types for label data
export interface LabelData {
  // Product Information
  product_name: string;
  product_chinese: string;
  product_sku: string;
  product_ingredient: string;

  // Lot and Traceability
  lot_number: string;
  box_number: number;
  barcode_number: string;

  // Weight Details
  actual_weight: string;
  target_weight: string;
  weight_unit: string;

  // Date and Time
  packed_date: string;
  packed_time: string;
  packed_timestamp: string;

  // Operator Info
  operator_id: string;
  operator_name: string;

  // Station Info
  station_number: string | number;
  station_name: string;

  // Traceability
  raw_material_lot: string;
  raw_material_type: string;

  // Production Location / Company
  production_location: string;

  // QR Code Data
  qr_data: string;
}

export interface SessionData {
  session_number?: string;
  product_name?: string;
  product_chinese?: string;
  product_sku?: string;
  product_ingredient?: string;
  station_number?: string | number;
  station_name?: string;
  raw_material_lot?: string;
  raw_material_name?: string;
  raw_material_item_number?: string;
}

export interface UserData {
  id?: string | null;
  fullName?: string | null;
  full_name?: string | null;
  username?: string | null;
}

/**
 * Generate label data for a packed box
 */
export const generateLabelData = (
  boxNumber: number,
  actualWeight: number,
  targetWeight: number,
  session: SessionData | null,
  user: UserData | null,
  productionLocation: string = 'T&B Food'
): LabelData => {
  const now = new Date();
  // Generate unique barcode number combining session, box, and timestamp
  const barcodeNumber = `${session?.session_number}-BOX${boxNumber.toString().padStart(3, '0')}-${now.getTime()}`;

  return {
    // Product Information
    product_name: session?.product_name || '',
    product_chinese: session?.product_chinese || '',
    product_sku: session?.product_sku || '',
    product_ingredient: session?.product_ingredient || '',

    // Lot and Traceability
    lot_number: session?.session_number || '',
    box_number: boxNumber,
    barcode_number: barcodeNumber,

    // Weight Details
    actual_weight: actualWeight.toFixed(2),
    target_weight: targetWeight.toFixed(2),
    weight_unit: 'lbs',

    // Date and Time
    packed_date: now.toLocaleDateString(),
    packed_time: now.toLocaleTimeString(),
    packed_timestamp: now.toISOString(),

    // Operator Info
    operator_id: user?.id || '',
    operator_name: user?.fullName || user?.full_name || user?.username || '',

    // Station Info
    station_number: session?.station_number || '',
    station_name: session?.station_name || '',

    // Traceability
    raw_material_lot: session?.raw_material_lot || '',
    raw_material_type: session?.raw_material_name || session?.raw_material_item_number || '',

    // Production Location / Company
    production_location: productionLocation,

    // QR Code Data (for scanning)
    qr_data: JSON.stringify({
      lot: session?.session_number,
      box: boxNumber,
      sku: session?.product_sku,
      weight: actualWeight.toFixed(2),
      date: now.toISOString(),
      operator: user?.id,
      barcode: barcodeNumber
    })
  };
};

/**
 * Generate barcode image as data URL
 * @param data Label data containing product info
 * @param prefetchedBarcode Optional pre-fetched barcode data (avoids extra API call)
 */
const generateBarcodeImage = async (
  data: LabelData,
  prefetchedBarcode?: BarcodeData
): Promise<{ dataUrl: string; displayText: string }> => {
  let barcodeDataUrl = '';
  let barcodeDisplayText = '';

  // Use pre-fetched barcode data if available
  if (prefetchedBarcode) {
    barcodeDisplayText = prefetchedBarcode.displayText || prefetchedBarcode.data;

    const canvas = document.createElement('canvas');
    JsBarcode(canvas, prefetchedBarcode.data, {
      format: prefetchedBarcode.format,
      width: 2,
      height: 50,
      displayValue: false,
      margin: 8
    });
    barcodeDataUrl = canvas.toDataURL('image/png');

    return { dataUrl: barcodeDataUrl, displayText: barcodeDisplayText };
  }

  // Fallback: fetch from API (should rarely happen with new flow)
  const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || '';
  try {
    const response = await fetch(
      `${apiBaseUrl}/api/product/barcode/product/${encodeURIComponent(data.product_sku)}?weight=${data.actual_weight}`
    );
    const result = await response.json();

    if (result.success) {
      // Store the human-readable format for display
      barcodeDisplayText = result.data.displayText || result.data.data;

      // Generate barcode using JsBarcode with the GS1-128 data (without display value)
      const canvas = document.createElement('canvas');
      JsBarcode(canvas, result.data.data, {
        format: result.data.format,
        width: 2,
        height: 50,
        displayValue: false,
        margin: 8
      });
      barcodeDataUrl = canvas.toDataURL('image/png');
    } else {
      throw new Error(result.error || 'Failed to generate barcode');
    }
  } catch (error) {
    console.error('Barcode generation failed:', error);
    // Fallback to lot number barcode if GS1-128 fails
    const canvas = document.createElement('canvas');
    JsBarcode(canvas, data.lot_number, {
      format: 'CODE128',
      width: 2,
      height: 50,
      displayValue: false,
      margin: 8
    });
    barcodeDataUrl = canvas.toDataURL('image/png');
    barcodeDisplayText = data.lot_number;
  }

  return { dataUrl: barcodeDataUrl, displayText: barcodeDisplayText };
};

/**
 * Get company info based on production location code
 * Supports both legacy names ('HTE Food', 'T&B Food') and location codes ('HTE', 'TNB', 'DSK')
 */
const getCompanyInfo = (productionLocation: string): { name: string; address: string; website: string } => {
  const loc = (productionLocation || '').toUpperCase();

  // HTE Food Corp
  if (loc === 'HTE' || loc === 'HTE FOOD') {
    return {
      name: 'HTE FOOD CORP.',
      address: 'COLLEGE POINT, NY 11356',
      website: 'https://wayfong.com'
    };
  }

  // Dim Sum King
  if (loc === 'DSK' || loc === 'DIM SUM KING') {
    return {
      name: 'DIM SUM KING',
      address: 'COLLEGE POINT, NY 11356',
      website: 'https://wayfong.com'
    };
  }

  // T&B Food Corp (default)
  return {
    name: 'T&B FOOD CORP.',
    address: 'COLLEGE POINT, NY 11356',
    website: 'https://wayfong.com'
  };
};

/**
 * Generate the HTML content for the label
 * @param isReprint - If true, shows "REPRINTED" banner at top
 */
const generateLabelHtml = (data: LabelData, barcodeDataUrl: string, barcodeDisplayText: string, isReprint: boolean = false): string => {
  // Extract batch/date code from AI(10) field for water box
  const batchMatch = (barcodeDisplayText || '').match(/\(10\)(\d+)/);
  const last5Digits = batchMatch ? batchMatch[1] : (data.lot_number || '').replace(/[^0-9]/g, '').slice(-5);

  // Get company info based on production location
  const companyInfo = getCompanyInfo(data.production_location);

  return `<!DOCTYPE html>
<html>
<head>
  <title>Product Label</title>
  <style>
    /* Force exact label size for all printers/browsers */
    @page {
      size: 4in 3in landscape !important;
      margin: 0 !important;
      padding: 0 !important;
    }
    @media print {
      @page {
        size: 4in 3in landscape !important;
        margin: 0 !important;
        padding: 0 !important;
      }
      * {
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
        color-adjust: exact !important;
      }
      html, body {
        width: 4in !important;
        height: 3in !important;
        margin: 0 !important;
        padding: 0 !important;
        overflow: hidden !important;
      }
      .label {
        width: 4in !important;
        height: 3in !important;
        max-height: 3in !important;
        page-break-inside: avoid !important;
        break-inside: avoid !important;
        overflow: hidden !important;
      }
    }
    * {
      box-sizing: border-box;
      margin: 0;
      padding: 0;
    }
    html {
      width: 4in;
      height: 3in;
      margin: 0;
      padding: 0;
    }
    body {
      font-family: 'Times New Roman', Times, serif;
      font-weight: 300;
      margin: 0;
      padding: 0;
      background: white;
      width: 4in;
      height: 3in;
      overflow: hidden;
    }
    .label {
      width: 4in;
      height: 3in;
      padding: 2mm;
      position: relative;
      font-size: 6pt;
      line-height: 1.1;
      overflow: hidden;
      /* Prevent any scaling */
      transform: none !important;
      transform-origin: top left;
      -webkit-transform-origin: top left;
      -webkit-transform: none !important;
    }
    .header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      margin-bottom: 0;
      gap: 3mm;
    }
    .header-left {
      flex: 1;
    }
    .header-right {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 1.5mm;
      flex-shrink: 0;
    }
    .product-code-row {
      display: flex;
      align-items: center;
      gap: 3mm;
      margin: 0 0 0.5mm 0;
    }
    .product-code {
      font-size: 17pt;
      font-weight: bold;
      margin: 0;
      line-height: 1.1;
      white-space: nowrap;
      overflow: hidden;
    }
    .product-chinese {
      font-size: 13pt;
      margin: 0 0 0.5mm 0;
      line-height: 1.1;
      font-family: 'Noto Sans CJK TC', 'Microsoft JhengHei', 'PingFang TC', 'SimSun', serif;
    }
    .product-english {
      font-size: 12pt;
      margin: 0;
      line-height: 1.1;
    }
    .usda-stamp {
      width: 24mm;
      height: 24mm;
      border: 1.5px solid #000;
      border-radius: 50%;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      text-align: center;
      font-size: 6.5pt;
      font-family: Arial, sans-serif;
      padding: 1mm;
      flex-shrink: 0;
      line-height: 1.05;
      overflow: hidden;
    }
    .usda-stamp .title {
      font-weight: bold;
      font-size: 8.5pt;
      margin-bottom: 0.2mm;
    }
    .usda-stamp .est {
      font-size: 8pt;
      margin-top: 0.5mm;
    }
    .content-row {
      display: flex;
      justify-content: space-between;
      margin-bottom: 1mm;
    }
    .left-column {
      flex: 1;
      padding-right: 0.5mm;
      max-width: calc(100% - 25mm);  /* Barcode almost touching USDA stamp */
    }
    .right-column {
      width: 35mm;
      text-align: right;
    }
    .barcode-section {
      margin: 0;
      text-align: left;
      width: 58mm;
    }
    .barcode-section img {
      display: block;
      width: 58mm;
      height: 7mm;
    }
    .barcode-number {
      font-size: 7pt;
      margin-top: 0.5mm;
      line-height: 1;
      text-align: justify;
      text-align-last: justify;
      font-family: 'Times New Roman', Times, serif;
      font-weight: normal;
      width: 58mm;
    }
    .water-content {
      font-size: 6pt;
      margin-bottom: 1mm;
      line-height: 1.1;
    }
    .water-box {
      border: 1.5px solid #000;
      padding: 0.5mm 1mm;
      display: inline-block;
      font-weight: normal;
      font-size: 10pt;
      margin-left: 15mm;
    }
    .keep-refrigerated {
      font-size: 10pt;
      font-weight: bold;
      margin: 2mm 0 0 0;
      line-height: 1.1;
      text-align: left;
    }
    .ingredient {
      font-size: 9pt;
      font-weight: normal;
      margin: 0 0 0.5mm 0;
      line-height: 1.1;
      white-space: normal;
      max-width: 37mm;
    }
    .bottom-row {
      display: flex;
      justify-content: space-between;
      margin: 0;
    }
    .company-info {
      flex: 1;
      font-size: 8pt;
      line-height: 1.2;
      padding-right: 2mm;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
    }
    .company-name {
      font-weight: bold;
      font-size: 8pt;
      margin-bottom: 0.3mm;
    }
    .made-in-usa {
      color: #FFFFFF;
    }
    .net-weight {
      font-size: 9pt;
      font-weight: bold;
      margin: 1mm 0;
    }
    .safe-handling {
      border: .5px solid #000;
      padding: 1mm;
      width: 65mm;
      font-family: Arial, sans-serif;
      margin-top: 0;
      margin-bottom: 0;
      margin-left: auto;
    }
    .safe-handling-title {
      font-weight: bold;
      text-align: center;
      font-size: 7.5pt;
      margin-bottom: 1.2mm;
    }
    .safe-handling-header {
      font-size: 4.5pt;
      margin-bottom: 1mm;
      line-height: 1.2;
      text-align: justify;
    }
    .safe-handling-item {
      display: flex;
      align-items: flex-start;
      margin: 0.4mm 0;
      font-size: 5.5pt;
      line-height: 1.2;
    }
    .safe-handling-icon {
      width: 2.75mm;
      height: 2.75mm;
      margin-right: 1.5mm;
      flex-shrink: 0;
      margin-top: 0.2mm;
    }
    .safe-handling-icon svg {
      width: 100%;
      height: 100%;
    }
    .reprint-badge {
      position: absolute;
      top: 10mm;
      left: 58mm;
      border: 0.8px solid #000;
      font-family: Arial, sans-serif;
      font-size: 8pt;
      font-weight: bold;
      width: 3.5mm;
      height: 3.5mm;
      display: flex;
      align-items: center;
      justify-content: center;
      line-height: 1;
    }
  </style>
</head>
<body>
  <div class="label">
    ${isReprint ? '<div class="reprint-badge">R</div>' : ''}
    <!-- Header with Product Info, Barcode, and USDA Stamp -->
    <div class="header">
      <div class="header-left">
        <div class="product-code-row">
          <div class="product-code">${data.product_sku || 'B HOMC t30'}</div>
          <div class="water-box">${last5Digits}</div>
        </div>
        <div class="product-chinese">${data.product_chinese || ''}</div>
        <div class="product-english">${data.product_name || 'Beef Omasum'}</div>
        <div class="barcode-section">
          <img src="${barcodeDataUrl}" alt="Barcode" />
          <div class="barcode-number">${barcodeDisplayText}</div>
        </div>
      </div>
      <div class="header-right">
        <div class="usda-stamp">
          <div class="title">U.S.</div>
          <div>INSPECTED</div>
          <div>AND PASSED BY</div>
          <div>DEPARTMENT OF</div>
          <div>AGRICULTURE</div>
          <div class="est">EST. 32138</div>
        </div>
      </div>
    </div>

    <!-- Keep Refrigerated -->
    <div class="keep-refrigerated">KEEP REFRIGERATED</div>

    <!-- Bottom Row: Company Info and Safe Handling -->
    <div class="bottom-row">
      <div class="company-info">
        <div class="ingredient">${data.product_ingredient ? `INGREDIENT: ${data.product_ingredient.split(' ').map((w: string) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ')}` : '&nbsp;'}</div>
        <div class="company-details">
          <div class="company-name">${companyInfo.name}</div>
          <div>${companyInfo.address}</div>
          <div>${companyInfo.website}</div>
          <div class="made-in-usa">Made in U.S.A.</div>
          <div class="net-weight">NET WT: ${data.actual_weight}${data.weight_unit}</div>
        </div>
      </div>

      <div class="safe-handling">
        <div class="safe-handling-title">Safe Handling Instructions</div>
        <div class="safe-handling-header">THIS PRODUCT WAS PREPARED FROM INSPECTED AND PASSED MEAT AND/OR POULTRY. SOME FOOD PRODUCTS MAY CONTAIN BACTERIA THAT COULD CAUSE ILLNESS IF THE PRODUCT IS MISHANDLED OR COOKED IMPROPERLY. FOR YOUR PROTECTION, FOLLOW THESE SAFE HANDLING INSTRUCTIONS.</div>

        <div class="safe-handling-item">
          <div class="safe-handling-icon">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <rect x="5" y="2" width="14" height="20" rx="1" stroke="currentColor" stroke-width="1.5" fill="none"/>
              <line x1="5" y1="6" x2="19" y2="6" stroke="currentColor" stroke-width="1.5"/>
              <line x1="8" y1="10" x2="10" y2="10" stroke="currentColor" stroke-width="1.5"/>
              <line x1="14" y1="10" x2="16" y2="10" stroke="currentColor" stroke-width="1.5"/>
              <line x1="8" y1="14" x2="10" y2="14" stroke="currentColor" stroke-width="1.5"/>
              <line x1="14" y1="14" x2="16" y2="14" stroke="currentColor" stroke-width="1.5"/>
              <line x1="8" y1="18" x2="10" y2="18" stroke="currentColor" stroke-width="1.5"/>
              <line x1="14" y1="18" x2="16" y2="18" stroke="currentColor" stroke-width="1.5"/>
            </svg>
          </div>
          <div>Keep refrigerated or frozen.</div>
        </div>

        <div class="safe-handling-item">
          <div class="safe-handling-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
              <path d="M14 14.76V3.5a2.5 2.5 0 0 0-5 0v11.26a4.5 4.5 0 1 0 5 0z"/>
              <circle cx="11.5" cy="18" r="2"/>
            </svg>
          </div>
          <div>Thaw in refrigerator or microwave.</div>
        </div>

        <div class="safe-handling-item">
          <div class="safe-handling-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
              <circle cx="12" cy="12" r="9"/>
              <line x1="12" y1="3" x2="12" y2="21"/>
              <line x1="3" y1="12" x2="21" y2="12"/>
            </svg>
          </div>
          <div>Keep raw meat and poultry separate from other foods.</div>
        </div>

        <div class="safe-handling-item">
          <div class="safe-handling-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
              <path d="M13 2v6h3l-4 5-4-5h3V2M5.5 20c.28 0 .5-.22.5-.5s-.22-.5-.5-.5-.5.22-.5.5.22.5.5.5m0 1c-.83 0-1.5-.67-1.5-1.5S4.67 18 5.5 18s1.5.67 1.5 1.5S6.33 21 5.5 21M12 13v-2M7.5 16.5L12 13l4.5 3.5"/>
            </svg>
          </div>
          <div>Wash working surfaces (including cutting boards), utensils, and hands after touching raw meat or poultry.</div>
        </div>

        <div class="safe-handling-item">
          <div class="safe-handling-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
              <path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z"/>
            </svg>
          </div>
          <div>Cook thoroughly.</div>
        </div>

        <div class="safe-handling-item">
          <div class="safe-handling-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
              <path d="M12 8c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4z"/>
              <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41"/>
            </svg>
          </div>
          <div>Keep hot foods hot. Refrigerate leftovers immediately or discard.</div>
        </div>
      </div>
    </div>
  </div>
</body>
</html>`;
};

/**
 * Print a product label using Blob URL approach
 * Uses iframe with Blob URL to avoid sandbox restrictions that block print()
 * @param data Label data containing product info
 * @param prefetchedBarcode Optional pre-fetched barcode data (avoids extra API call)
 * @param isReprint If true, shows "REPRINTED" banner at top of label
 */
export const printLabel = async (data: LabelData, prefetchedBarcode?: BarcodeData, isReprint: boolean = false): Promise<void> => {
  // Generate barcode image (uses pre-fetched data if available)
  const { dataUrl: barcodeDataUrl, displayText: barcodeDisplayText } = await generateBarcodeImage(data, prefetchedBarcode);

  // Generate the HTML content
  const htmlContent = generateLabelHtml(data, barcodeDataUrl, barcodeDisplayText, isReprint);

  // Create a Blob URL from the HTML content (avoids srcdoc sandbox restrictions)
  const blob = new Blob([htmlContent], { type: 'text/html' });
  const blobUrl = URL.createObjectURL(blob);

  // Create a hidden iframe for printing
  const printFrame = document.createElement('iframe');
  printFrame.style.position = 'absolute';
  printFrame.style.width = '0';
  printFrame.style.height = '0';
  printFrame.style.border = 'none';
  printFrame.style.visibility = 'hidden';

  // Use Blob URL instead of srcdoc to avoid sandbox blocking print()
  printFrame.src = blobUrl;

  // Append to body
  document.body.appendChild(printFrame);

  // Wait for the iframe to load, then print
  printFrame.onload = () => {
    // Small delay to ensure styles are applied
    setTimeout(() => {
      printFrame.contentWindow?.print();

      // Remove iframe and revoke Blob URL after printing
      setTimeout(() => {
        document.body.removeChild(printFrame);
        URL.revokeObjectURL(blobUrl);
      }, 100);
    }, 250);
  };
};

/**
 * Print multiple copies of a label
 */
export const printLabelCopies = async (data: LabelData, copies: number): Promise<void> => {
  for (let i = 0; i < copies; i++) {
    await new Promise<void>((resolve) => {
      setTimeout(async () => {
        await printLabel(data);
        resolve();
      }, i * 300); // Slight delay between prints
    });
  }
};

/**
 * Generate label HTML for preview purposes (used when Simulate Printer is ON)
 * Returns the HTML string that can be displayed in an iframe for previewing
 * @param data Label data containing product info
 * @param prefetchedBarcode Optional pre-fetched barcode data
 * @param isReprint If true, shows "REPRINTED" banner at top of label
 */
export const generateLabelPreviewHtml = async (
  data: LabelData,
  prefetchedBarcode?: BarcodeData,
  isReprint: boolean = false
): Promise<string> => {
  const { dataUrl: barcodeDataUrl, displayText: barcodeDisplayText } = await generateBarcodeImage(data, prefetchedBarcode);
  return generateLabelHtml(data, barcodeDataUrl, barcodeDisplayText, isReprint);
};

export default {
  generateLabelData,
  printLabel,
  printLabelCopies,
  generateLabelPreviewHtml,
};
